import { PropsWithChildren } from 'react';

import gql from 'graphql-tag';
import { useRouter } from 'next/router';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';

import { contentFulClient } from './blog/[postSlug]';
import WhySkyhook from '@website/components/about/WhySkyhook';
import ContentfulContent from '@website/components/Contentful/ContentfulContent';
import HeaderSection from '@website/components/HeaderSection';
import DefaultLayout from '@website/components/layouts/DefaultLayout';
import { SalesPopupProvider } from '@website/components/SalesPopupProvider';
import SeoMetaTags from '@website/components/seoMetaTags';
import { ContentFulPage } from '@website/components/StaticPageContent/StaticPageContent';
import TrustPilotWidget from '@website/components/trustpilot/TrustPilotWidget';
import LoadingPage from '@website/components/utilities/LoadingPage';
import NotFound from '@website/components/utilities/NotFound';




export const aboutUsPageQuery = gql`
  query ($pageSlug: String!) {
    pagesCollection(limit: 1, where: { slug: $pageSlug }) {
      items {
        slug
        title
        image {
          url
          title
        }
        description
        keywords
        content {
          json
          links {
            assets {
              block {
                url
                sys {
                  id
                }
                title
                description
              }
            }
          }
        }
        metaTitle
        description
        keywords
      }
    }
  }
`;

export async function getStaticProps() {
  const res = await contentFulClient.query({
    query: aboutUsPageQuery,
    variables: {
      pageSlug: 'about',
    },
    fetchPolicy: 'no-cache',
  });

  // If the page doesn't exist, return 404
  if (!res.data.pagesCollection?.items?.[0]) {
    return {
      notFound: true,
    };
  }

  if (res.errors) {
    console.error(res.errors);
    throw new Error('Failed to fetch API');
  }

  const pageContent = (await res.data.pagesCollection
    ?.items?.[0]) as ContentFulPage;

  return {
    props: {
      page: pageContent,
    },
    revalidate: 2,
  };
}

type StaticPagesProps = {
  page: ContentFulPage;
};

export default function StaticPages({ page }: StaticPagesProps) {
  const router = useRouter();

  // show loading page if the page hasn't been generated, fallback will generate
  // send it to the frontend
  if (router.isFallback) {
    return <LoadingPage />;
  }

  if (!page) {
    return <NotFound />;
  }

  return (
    <>
      <SeoMetaTags
        title={page.metaTitle || page.title}
        description={page.description}
        keywords={page.keywords ?? []}
        showWebsiteName={false}
        imageKey={page.image?.url}
        cdn={process.env.NEXT_PUBLIC_CONTENTFUL_IMGIX_URL}
      />

      {page.image?.url && (
        <HeaderSection title={page.title} image={page.image.url} />
      )}

      <style jsx>{`
        .breadcrumb {
          font-size: 16px;
        }
      `}</style>

      <section className="py-4 bg-gray-100">
        <Container>
          <TrustPilotWidget
            templateId="5419b6ffb0d04a076446a9af"
            height="20px"
            className="mx-auto"
          />
        </Container>
      </section>

      <section>
        <Container>
          <Row>
            <Col xs={12} sm={8} md={7} className="mx-auto pt-2">
              <Breadcrumb className="py-2">
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item active>{page.title}</Breadcrumb.Item>
              </Breadcrumb>
              <p className="lead">{page.description}</p>
            </Col>
          </Row>
        </Container>
      </section>

      <section>
        <Container>
          <ContentfulContent document={page} />
        </Container>
      </section>

      <WhySkyhook />

      <SalesPopupProvider bottomPosition={10} />
    </>
  );
}

StaticPages.Layout = function Layout({ children }: PropsWithChildren<{}>) {
  return <DefaultLayout className="">{children}</DefaultLayout>;
};
